import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../assets/images/logo.jpg';
import QRCode from 'qrcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';

const MarriageCertificateList = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    // Calculate new entries count
    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    // Fuse.js for fuzzy search
    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['token_id', 'local_no', 'name', 'father_name', 'cnic', 'phone_no', 'status'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            console.log("Fetching marriage certificate entries...");
            const response = await axios.post('https://chaman.gob.pk/eac_backend/marriagecert.php', {
                action: 'get_records',
            });

            console.log("Marriage cert response:", response.data);

            // Handle both response formats
            let dataArray = [];
            
            if (response.data && response.data.status === "success" && Array.isArray(response.data.data)) {
                // Format 1: {status: "success", data: [...]}
                dataArray = response.data.data;
            } else if (Array.isArray(response.data)) {
                // Format 2: Direct array [...]
                dataArray = response.data;
            } else {
                console.error('Unexpected response format:', response.data);
                setError('Unexpected data format received from server');
                setGdsrEntries([]);
                return;
            }

            if (dataArray && dataArray.length > 0) {
                const now = new Date();
                setLastRefreshTime(now);

                // Parse and attach timestamp, then sort by newest date+time
                const sortedEntries = dataArray
                    .map(entry => {
                        // Try to parse the date reliably
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp); // Newest first

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.log('No data received or empty array');
            }
        } catch (err) {
            console.error('Error fetching Marriage Certificate entries:', err);
            setError('Failed to fetch Marriage Certificate entries: ' + (err.message || 'Network error'));
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        // Initial load with small delay to show loading state
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/marriagecert.php', {
                    action: 'delete',
                    token_id,
                });
                
                // Handle both response formats
                const isSuccess = response.data.status === 'success' || 
                                 (response.data && response.data.includes && response.data.includes('success'));
                
                if (isSuccess) {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                    alert('Record deleted successfully');
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                console.error('Delete error:', error);
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-marriage-certificate', { state: { entry } });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const [year, month, day] = dateString.split('-');
            if (day && month && year) {
                return `${day}-${month}-${year}`;
            }
            return dateString;
        } catch (e) {
            return dateString;
        }
    };

    const handlePrint = async (entry) => {
        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
        
            // Function to format date as "DD-MM-YYYY"
            const formatDateForPrint = (dateString) => {
                if (!dateString) return 'N/A';
                try {
                    const [year, month, day] = dateString.split('-');
                    if (day && month && year) {
                        return `${day}-${month}-${year}`;
                    }
                    return dateString;
                } catch (e) {
                    return dateString;
                }
            };
        
            const formattedDate = formatDateForPrint(entry.date);
            const formattedMarriageDate = formatDateForPrint(entry.dob);
        
            // Add logo image and text
            doc.setFontSize(10);
            const imageWidth = 0.8 * 28.5;
            const imageHeight = 0.6 * 28.8;
            const xPosition = 14;
            const yPosition = 16;
            doc.addImage(logoImage, "PNG", xPosition, yPosition, imageWidth, imageHeight);
        
            const maxTextWidth = Math.max(
                doc.getTextWidth(`No DC (Marriage-Certificate) /2023/ ${entry.local_no || ''}`),
                doc.getTextWidth("OFFICE OF THE"),
                doc.getTextWidth("DEPUTY COMMISSIONER CHAMAN"),
                doc.getTextWidth(`Dated the ${formattedDate}`)
            );
            const rightAlignX = pageWidth - maxTextWidth - 20;
        
            // Header Section
            doc.setFontSize(11);
            doc.setFont('times',"normal");
            doc.text(`No DC (Marriage-Certificate) /2023/ ${entry.local_no || ''}`, rightAlignX, 18);
            doc.text("OFFICE OF THE", rightAlignX + maxTextWidth / 2 - doc.getTextWidth("OFFICE OF THE") / 2, 23);
            doc.text("DEPUTY COMMISSIONER CHAMAN", rightAlignX + maxTextWidth / 2 - doc.getTextWidth("DEPUTY COMMISSIONER CHAMAN") / 2, 28);
            doc.text(`Dated the ${formattedDate}`, rightAlignX + maxTextWidth / 2 - doc.getTextWidth(`Dated the ${formattedDate}`) / 2, 33);
        
            // Body Content
            doc.setFontSize(12);
            doc.text("To,", 14, 47);
            doc.text("The Chief Officer,", 43.5, 55);
            doc.text("Municipal Corporation Chaman.", 43.5, 60);
        
            doc.setFontSize(13);
            doc.text("Subject:", 14, 70);
            doc.setFont("times", "bold");
            doc.text("APPLICATION FORM FOR ISSUANCE OF MARRIAGE CERTIFICATE", 43.5, 70);
            doc.setFont("times", "normal");
            doc.line(43.5, 71, 195, 71);
        
            doc.setFontSize(11);
            doc.text("Reference to your office letter No: 17/MCC dated 09-05-2018 on the subject cited above.", 43.5, 78);
            doc.text("In pursuance of Principal Secretary to Chief Minister, Balochistan letter No. PS-CM/1-1/2014/", 43.5, 90);
            doc.text("1983/87 dated 10-04-2014, the undersigned is pleased to accord approval for issuance of MARRIAGE certificate", 14, 95);
            doc.text("in favor of the following, and subject to completion of Codal formalities / necessary documents as prescribed /", 14, 100);
            doc.text("deposit of Government fee and also keep office record in safe hands.", 14, 105);
        
            // Table Data
            const tableData = [
                ["Sr#", "Husband & Father Name", "Wife & Father Name", "CNIC", "Date of Marriage", "Remarks"],
                ["1", entry.name || '', entry.father_name || '', entry.cnic || '', formattedMarriageDate, entry.remarks || ""],
            ];
        
            doc.autoTable({
                head: [tableData[0]],
                body: tableData.slice(1),
                startY: 120,
                theme: "grid",
                headStyles: {
                    fillColor: [0, 0, 0],
                    textColor: [255, 255, 255],
                    fontSize: 10,
                    fontStyle: "bold",
                    halign: "center",
                },
                styles: {
                    fontSize: 10,
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1,
                },
                columnStyles: {
                    0: { cellWidth: 10, halign: "center" },
                    1: { cellWidth: 35, halign: "left" },
                    2: { cellWidth: 35, halign: "left" },
                    3: { cellWidth: 37, halign: "center" },
                    4: { cellWidth: 25, halign: "center" },
                    5: { cellWidth: 38, halign: "center" },
                },
            });
        
            // Footer Section
            const footerY = Math.max(doc.autoTable.previous.finalY + 5, pageHeight - 130);
            const deputyTextX = 128;
            const deputyTextY = footerY;
        
            doc.setFontSize(10).setFont("helvetica", "normal");
            doc.text("ADDITIONAL DEPUTY COMMISSIONER", deputyTextX, deputyTextY);
            doc.text("CHAMAN", 155, footerY + 5);
        
            // QR Code Generation
            const qrData = `Marriage certificate of ${entry.name || ''} has been verified and issued by the Office of the Deputy Commissioner, Chaman.`;
            const qrCanvas = document.createElement("canvas");
        
            await QRCode.toCanvas(qrCanvas, qrData, { width: 100 });
            const qrDataURL = qrCanvas.toDataURL("image/png");
        
            doc.addImage(qrDataURL, "PNG", 100, pageHeight - 80, 20, 20);
        
            // Open PDF in a new tab
            window.open(doc.output("bloburl"));
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Error generating PDF: " + error.message);
        }
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(gdsrEntries);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Marriage Certificate Entries');
        XLSX.writeFile(workbook, `marriage_certificate_records_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={style} className="table-row">
                <div className="table-cell">{entry.token_id || 'N/A'}</div>
                <div className="table-cell">{formatDate(entry.date)}</div>
                <div className="table-cell">{entry.name || 'N/A'}</div>
                <div className="table-cell">{entry.father_name || 'N/A'}</div>
                <div className="table-cell">{entry.cnic || 'N/A'}</div>
                <div className="table-cell actions btn-group">
                    <button onClick={() => handlePrint(entry)} title="Print" className="print-btn">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => handleEdit(entry)} title="Edit" className="edit-btn">
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading Marriage Certificate Records...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <div className="error-card">
                <h3>Error Loading Data</h3>
                <p>{error}</p>
                <div className="error-actions">
                    <button onClick={refreshData} className="retry-btn">
                        <FontAwesomeIcon icon={faSyncAlt} /> Retry
                    </button>
                    <button onClick={() => window.location.reload()} className="reload-btn">
                        Reload Page
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="certificate-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    <h2>Marriage Certificate Records</h2>
                    <div className="stats-container">
                        <span className="entries-count">Total: {gdsrEntries.length}</span>
                        {newEntriesCount > 0 && (
                            <span className="new-entries-count">New: {newEntriesCount}</span>
                        )}
                        {lastRefreshTime && (
                            <span className="refresh-time">
                                Last updated: {lastRefreshTime.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                </div>

                <div className="header-right">
                    <div className="search-refresh-wrapper">
                        <button
                            onClick={refreshData}
                            className="refresh-button"
                            disabled={isRefreshing}
                            title="Refresh data"
                        >
                            <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={isRefreshing}
                                className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                            />
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>

                        <div className="search-bar">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search by Token ID, Name, CNIC..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')}>
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="table-container">
                <div className="table-header">
                    <div className="header-cell">Token ID</div>
                    <div className="header-cell">Date</div>
                    <div className="header-cell">Name</div>
                    <div className="header-cell">F/H Name</div>
                    <div className="header-cell">CNIC</div>
                    <div className="header-cell">Actions</div>
                </div>

                <div className="table-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={45}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                            {searchTerm ? (
                                <>
                                    <FontAwesomeIcon icon={faSearch} size="3x" />
                                    <p>No records found matching "{searchTerm}"</p>
                                    <button onClick={() => setSearchTerm('')} className="clear-search-btn">
                                        Clear Search
                                    </button>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faInfoCircle} size="3x" />
                                    <p>No marriage certificate records available</p>
                                    <p className="empty-subtext">Add new records using the Marriage Certificate form</p>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="table-footer">
                    <div className="footer-info">
                        Showing {filteredEntries.length} of {gdsrEntries.length} entries
                        {searchTerm && ` (filtered)`}
                    </div>
                    <div className="footer-actions">
                        <button 
                            onClick={exportToExcel}
                            className="export-btn"
                        >
                            <FontAwesomeIcon icon={faFileExcel} /> Export to Excel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MarriageCertificateList;